const HERO_FIXED_PANEL_CLASS = "marketplace-facet-dropdown__panel--hero-fixed";

interface FacetDropdownEntry {
    dropdown: HTMLElement;
    trigger: HTMLButtonElement;
    panel: HTMLElement;
}

/** Panels clipped by overflow (hero bar or action-bar horizontal scroll). */
function usesFixedPanels(dropdown: HTMLElement): boolean {
    return (
        dropdown.closest("#hero-filter-bar") !== null ||
        dropdown.closest(".marketplace-action-bar__chips") !== null
    );
}

function positionFixedPanel(
    trigger: HTMLButtonElement,
    panel: HTMLElement,
): void {
    const rect = trigger.getBoundingClientRect();
    const panelWidth = Math.min(352, window.innerWidth - 24);
    const top = rect.bottom + 8;
    const maxHeight = Math.min(352, window.innerHeight - top - 16);

    panel.classList.add(HERO_FIXED_PANEL_CLASS);
    panel.style.width = `${panelWidth}px`;
    panel.style.maxHeight = `${maxHeight}px`;
    panel.style.left = `${Math.max(
        12,
        Math.min(rect.left, window.innerWidth - panelWidth - 12),
    )}px`;
    panel.style.top = `${top}px`;
}

function resetHeroFixedPanel(panel: HTMLElement): void {
    panel.classList.remove(HERO_FIXED_PANEL_CLASS);
    panel.style.width = "";
    panel.style.maxHeight = "";
    panel.style.left = "";
    panel.style.top = "";
}

/**
 * Tablet+ facet dropdowns in the marketplace action bar (e.g. Marque on /marketplace2).
 */
export function initActionBarFacetDropdowns(): void {
    const entries: FacetDropdownEntry[] = [];

    document.querySelectorAll<HTMLElement>("[data-facet-dropdown]").forEach(
        (dropdown) => {
            const trigger = dropdown.querySelector<HTMLButtonElement>(
                "[data-facet-dropdown-trigger]",
            );
            const panel = dropdown.querySelector<HTMLElement>(
                "[data-facet-dropdown-panel]",
            );
            if (!trigger || !panel) return;
            entries.push({ dropdown, trigger, panel });
        },
    );

    if (!entries.length) return;

    const heroFixedPanels = new Map<HTMLElement, HTMLButtonElement>();
    const heroPanelAnchors = new Map<HTMLElement, Comment>();

    const portalHeroPanel = (panel: HTMLElement): void => {
        if (heroPanelAnchors.has(panel)) {
            return;
        }

        const anchor = document.createComment("hero-facet-panel");
        panel.parentNode?.insertBefore(anchor, panel);
        heroPanelAnchors.set(panel, anchor);
        document.body.appendChild(panel);
    };

    const restoreHeroPanel = (panel: HTMLElement): void => {
        const anchor = heroPanelAnchors.get(panel);
        if (!anchor?.parentNode) {
            return;
        }

        anchor.parentNode.insertBefore(panel, anchor);
        anchor.remove();
        heroPanelAnchors.delete(panel);
    };

    const syncHeroFixedPanels = (): void => {
        heroFixedPanels.forEach((trigger, panel) => {
            if (!panel.hidden) {
                positionFixedPanel(trigger, panel);
            }
        });
    };

    const releaseHeroPanel = (panel: HTMLElement): void => {
        resetHeroFixedPanel(panel);
        restoreHeroPanel(panel);
        heroFixedPanels.delete(panel);
    };

    const closeAll = (except?: HTMLElement) => {
        entries.forEach(({ dropdown, trigger, panel }) => {
            if (dropdown === except) return;
            panel.hidden = true;
            trigger.setAttribute("aria-expanded", "false");
            releaseHeroPanel(panel);
        });
    };

    const isClickInsideDropdown = (
        { dropdown, panel }: FacetDropdownEntry,
        target: Node,
    ): boolean => {
        if (dropdown.contains(target)) {
            return true;
        }

        return !panel.hidden && panel.contains(target);
    };

    entries.forEach(({ dropdown, trigger, panel }) => {
        trigger.addEventListener("click", (event) => {
            event.stopPropagation();
            const willOpen = panel.hidden;
            closeAll(willOpen ? dropdown : undefined);
            panel.hidden = !willOpen;
            trigger.setAttribute("aria-expanded", String(willOpen));

            if (willOpen && usesFixedPanels(dropdown)) {
                portalHeroPanel(panel);
                heroFixedPanels.set(panel, trigger);
                positionFixedPanel(trigger, panel);
            } else {
                releaseHeroPanel(panel);
            }
        });

        panel.addEventListener("click", (event) => {
            event.stopPropagation();
        });
    });

    window.addEventListener("resize", syncHeroFixedPanels);
    window.addEventListener("scroll", syncHeroFixedPanels, true);

    // Horizontal slide of the action-bar chips must dismiss open facet panels
    // (otherwise they stay anchored while their trigger scrolls away).
    document
        .querySelectorAll<HTMLElement>(".marketplace-action-bar__chips")
        .forEach((chipsEl) => {
            chipsEl.addEventListener(
                "scroll",
                () => {
                    closeAll();
                },
                { passive: true },
            );
        });

    document.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof Node)) return;
        const clickedInside = entries.some((entry) =>
            isClickInsideDropdown(entry, target),
        );
        if (!clickedInside) closeAll();
    });
    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") closeAll();
    });
}
